import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Loader2, AlertCircle, Sun, Moon, Package, DollarSign, Wrench, X, ArrowLeft, ChevronDown, ChevronUp, Download, Check, ChevronLeft, ChevronRight, User } from 'lucide-react';
import { supabase, API_URL } from '../services/supabase';
import AuthModal from './AuthModal';
import UserProfile from './UserProfile';
import PricingModal from './PricingModal';

const BreakdownAI = () => {
  const [isDarkMode, setIsDarkMode] = useState(true); // Dark mode to match portfolio
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreviews, setImagePreviews] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [detectedObjects, setDetectedObjects] = useState(null);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [imageDataArray, setImageDataArray] = useState([]);
  const [currentStep, setCurrentStep] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isResearchingPrices, setIsResearchingPrices] = useState(false);
  // Remove premium research toggle state since we'll have separate buttons
  const [researchProgress, setResearchProgress] = useState('');
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [selectedComponentSource, setSelectedComponentSource] = useState(null);
  
  // Authentication state
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isPostAnalysisPrompt, setIsPostAnalysisPrompt] = useState(false);

  // Anonymous usage tracking - allow 1 free analysis before requiring sign-in
  const [anonymousUsageCount, setAnonymousUsageCount] = useState(() => {
    const stored = parseInt(localStorage.getItem('breakdownai_anonymous_usage') || '0');
    return isNaN(stored) ? 0 : stored;
  });
  const ANONYMOUS_USE_LIMIT = 1;
  
  const fileInputRef = useRef(null);

  // Authentication state management (Supabase)
  useEffect(() => {
    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Authentication handlers
  const handleAuthSuccess = (user) => {
    setUser(user);
    console.log('Authentication successful:', user.email);
  };

  const handleSignOut = () => {
    setUser(null);
  };

  const checkAuthAndProceed = (action) => {
    if (!user && anonymousUsageCount >= ANONYMOUS_USE_LIMIT) {
      setIsPostAnalysisPrompt(true);
      setShowAuthModal(true);
      return false;
    }
    return true;
  };

  // Helper function for authenticated API calls
  const makeAuthenticatedRequest = async (url, options = {}) => {
    const headers = { ...options.headers };

    if (user) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    }

    return fetch(url, {
      ...options,
      headers
    });
  };

  // Validate image file
  const validateImage = (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid image format. Please use JPG, PNG, or WebP.');
      return false;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be under 10MB.');
      return false;
    }
    
    return true;
  };

  // Handle multiple file uploads
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check authentication first
    if (!checkAuthAndProceed()) return;
    
    if (files.length > 4) {
      setError('Maximum 4 images allowed. Please select up to 4 images of different angles.');
      return;
    }
    
    // Validate all images
    for (const file of files) {
      if (!validateImage(file)) return;
    }
    
    setError('');
    
    // Process all images
    const previews = [];
    const processPromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          resolve();
        };
        reader.readAsDataURL(file);
      });
    });
    
    await Promise.all(processPromises);
    setImagePreviews(previews);
    setCurrentImageIndex(0); // Reset to first image
    
    identifyObjects(files);
  };

  // Step 1: Identify objects in image
  const identifyObjects = async (files) => {
    setIsLoading(true);
    setAnalysisResult(null);
    setDetectedObjects(null);
    
    try {
      setCurrentStep(`Uploading ${files.length} image${files.length > 1 ? 's' : ''}...`);
      
      // Create FormData to send multiple files
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append(`image_${index}`, file);
      });
      formData.append('imageCount', files.length);
      
      setCurrentStep('Identifying objects with AI...');
      
      // Send to backend API for object identification
      const response = await makeAuthenticatedRequest(`${API_URL}/identify-objects`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Object identification failed');
      }
      
      const result = await response.json();
      
      if (result.success) {
        if (result.singleObject) {
          // Single object detected, proceed directly to analysis with proper imageData
          setImageDataArray(result.imageDataArray);
          setCurrentStep('Analyzing single object...');
          await performAnalysis('Single Object', result.imageDataArray);
        } else {
          // Multiple objects detected, show selection
          setDetectedObjects(result.objects);
          setSelectedObjects([]); // Reset selected objects
          setImageDataArray(result.imageDataArray);
          setCurrentStep('');
          setIsLoading(false); // Stop loading to show object selection
        }
      } else {
        throw new Error(result.message || 'Object identification failed');
      }
      
    } catch (err) {
      console.error('Object identification error:', err);
      
      // Handle specific error types
      if (err.message.includes('rate limit')) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError(err.message || 'Failed to identify objects. Please try again.');
      }
      setIsLoading(false);
      setCurrentStep('');
    }
  };

  // Handle object selection toggle
  const toggleObjectSelection = (objectName) => {
    setSelectedObjects(prev => {
      if (prev.includes(objectName)) {
        return prev.filter(name => name !== objectName);
      } else {
        return [...prev, objectName];
      }
    });
  };

  // Unified analysis function
  const performAnalysis = async (objectsText, imageDataToUse = null) => {
    try {
      const dataToUse = imageDataToUse || imageDataArray;
      
      if (!dataToUse || dataToUse.length === 0) {
        throw new Error('No image data available for analysis');
      }

      const response = await makeAuthenticatedRequest(`${API_URL}/analyze-object`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          imageDataArray: dataToUse,
          selectedObject: objectsText
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Analysis failed');
      }
      
      const result = await response.json();
      
      if (result.success) {
        setAnalysisResult(result.data);

        // Don't auto-start price research - wait for user to click button

        // Track anonymous usage and prompt sign-in after first free analysis
        if (!user) {
          const newCount = anonymousUsageCount + 1;
          setAnonymousUsageCount(newCount);
          localStorage.setItem('breakdownai_anonymous_usage', newCount.toString());
          // Show auth modal after a brief delay so user can see results first
          setTimeout(() => {
            setIsPostAnalysisPrompt(true);
            setShowAuthModal(true);
          }, 2000);
        }
      } else {
        throw new Error(result.message || 'Analysis failed');
      }
      
      setCurrentStep('');
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze objects. Please try again.');
    } finally {
      setIsLoading(false);
      setCurrentStep('');
    }
  };

  // Confirm selected objects and proceed to analysis
  const confirmSelectedObjects = async () => {
    if (selectedObjects.length === 0) {
      setError('Please select at least one object to analyze.');
      return;
    }

    setIsLoading(true);
    setDetectedObjects(null);
    setError('');
    
    const objectsText = selectedObjects.length === 1 
      ? selectedObjects[0] 
      : selectedObjects.join(', ');
    
    setCurrentStep(`Analyzing ${selectedObjects.length} selected object${selectedObjects.length > 1 ? 's' : ''}...`);
    
    await performAnalysis(objectsText);
  };


  // Calculate total components - use absolute counting (count materials that can't be counted as 1)
  const getTotalComponents = (data) => {
    return data.main_categories.reduce((total, cat) => 
      total + cat.components.reduce((sum, comp) => {
        // If quantity is fractional or the unit suggests it can't be counted individually, count as 1
        if (comp.quantity < 1 || comp.unit === 'meters' || comp.unit === 'grams' || comp.unit === 'kilograms') {
          return sum + 1;
        }
        return sum + Math.ceil(comp.quantity); // Use ceiling for partial quantities
      }, 0), 0
    );
  };

  // Toggle theme
  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  // Slideshow navigation
  const nextImage = useCallback(() => {
    if (imagePreviews.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % imagePreviews.length);
    }
  }, [imagePreviews.length]);

  const prevImage = useCallback(() => {
    if (imagePreviews.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + imagePreviews.length) % imagePreviews.length);
    }
  }, [imagePreviews.length]);

  // Keyboard navigation for slideshow
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (analysisResult && imagePreviews.length > 1) {
        if (event.key === 'ArrowLeft') {
          prevImage();
        } else if (event.key === 'ArrowRight') {
          nextImage();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [analysisResult, imagePreviews.length, prevImage, nextImage]);

  // Start real price research with async job polling
  const startPriceResearch = async (bomData) => {
    // Premium research always requires authentication
    if (!user) {
      setIsPostAnalysisPrompt(true);
      setShowAuthModal(true);
      return;
    }

    setIsResearchingPrices(true);
    setResearchProgress('Starting real price research...');

    try {
      console.log('Starting real price research...');

      const endpoint = '/premium-research';

      // Start the research job
      const response = await makeAuthenticatedRequest(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bomData })
      });

      if (!response.ok) {
        if (response.status === 401) {
          setIsPostAnalysisPrompt(true);
          setShowAuthModal(true);
          throw new Error('Please sign in to research real prices');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to start research job');
      }
      
      const result = await response.json();
      
      if (result.success && result.jobId) {
        console.log(`Research job ${result.jobId} started, polling for status...`);
        setResearchProgress(`Job started - estimated ${result.estimatedTime}`);
        
        // Start polling for job status
        pollJobStatus(result.jobId);
      } else {
        throw new Error(result.message || 'Failed to start research job');
      }
      
    } catch (error) {
      console.error('Failed to start price research:', error);
      setResearchProgress('Failed to start research - keeping original prices');
      setIsResearchingPrices(false);
    }
  };

  // Poll job status until completion
  const pollJobStatus = async (jobId) => {
    const maxAttempts = 120; // 10 minutes at 5-second intervals
    let attempts = 0;
    
    const poll = async () => {
      try {
        attempts++;
        
        const response = await makeAuthenticatedRequest(`${API_URL}/job-status/${jobId}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Research job not found');
          }
          throw new Error('Failed to get job status');
        }
        
        const status = await response.json();
        console.log(`Job ${jobId} status: ${status.status} (${status.progress}%)`);
        
        // Update progress message
        if (status.message) {
          setResearchProgress(`${status.message} (${status.progress}%)`);
        }
        
        if (status.status === 'completed' && status.result) {
          // Research completed successfully
          console.log('Price research completed successfully');
          
          if (status.result.success && status.result.updatedBOM) {
            setAnalysisResult(status.result.updatedBOM);
            setResearchProgress('Research completed successfully!');
            
            // Clear progress message after 3 seconds
            setTimeout(() => setResearchProgress(''), 3000);
          } else {
            setResearchProgress('Research completed but no price updates available');
          }
          
          setIsResearchingPrices(false);
          return;
          
        } else if (status.status === 'failed') {
          // Research failed permanently
          console.error('Price research failed:', status.error);
          setResearchProgress(`Research failed: ${status.error || 'Unknown error'}`);
          setIsResearchingPrices(false);
          return;
          
        } else if (attempts >= maxAttempts) {
          // Timeout
          console.error('Research job timed out');
          setResearchProgress('Research timed out - keeping original prices');
          setIsResearchingPrices(false);
          return;
        }
        
        // Continue polling for jobs that are queued or processing
        if (status.status === 'queued' || status.status === 'processing') {
          setTimeout(poll, 5000); // Poll every 5 seconds
        }
        
      } catch (error) {
        console.error('Error polling job status:', error);
        attempts++;
        
        if (attempts >= maxAttempts) {
          setResearchProgress('Research monitoring failed - please check results manually');
          setIsResearchingPrices(false);
        } else {
          // Retry polling
          setTimeout(poll, 5000);
        }
      }
    };
    
    // Start polling
    poll();
  };

  // Reset to main screen
  const resetToMain = () => {
    setAnalysisResult(null);
    setDetectedObjects(null);
    setSelectedObjects([]);
    setImageDataArray([]);
    setImagePreviews([]);
    setCurrentImageIndex(0);
    setError('');
    setCurrentStep('');
    setIsLoading(false);
    setExpandedCategories({});
    setIsResearchingPrices(false);
    setResearchProgress('');
  };

  // Handle showing source details modal
  const showSourceDetails = (component) => {
    if (!component.price_updated) return;
    
    let sourceData = {
      component: component,
      researchType: component.research_type || 'standard'
    };
    
    if (component.research_type === 'premium') {
      // Find supplier data for this component
      const supplierData = analysisResult.supplier_data?.find(s => 
        s.component_name === component.name
      );
      sourceData.suppliers = supplierData?.suppliers || [];
    }
    
    setSelectedComponentSource(sourceData);
    setShowSourceModal(true);
  };

  const closeSourceModal = () => {
    setShowSourceModal(false);
    setSelectedComponentSource(null);
  };

  // Helper function to find supplier data for a component
  const findSupplierData = (supplierDataArray, category, componentIndex) => {
    if (!supplierDataArray) return null;
    
    // Calculate global component index
    let globalIndex = 0;
    const categories = analysisResult.main_categories;
    
    for (let catIdx = 0; catIdx < categories.length; catIdx++) {
      if (categories[catIdx] === category) {
        globalIndex += componentIndex;
        break;
      }
      globalIndex += categories[catIdx].components.length;
    }
    
    const supplierData = supplierDataArray.find(s => s.component_index === globalIndex);
    if (!supplierData || !supplierData.suppliers) return null;
    
    return {
      supplier1: supplierData.suppliers[0] || {},
      supplier2: supplierData.suppliers[1] || {}
    };
  };

  // Export to CSV with BOM levels (SAP style)
  const exportToCSV = () => {
    if (!analysisResult) return;

    const csvRows = [];
    // Enhanced CSV Headers for premium research
    const isPremiumData = analysisResult.supplier_data && analysisResult.supplier_data.length > 0;
    
    if (isPremiumData) {
      csvRows.push([
        'BOM Level',
        'Category', 
        'Component Name',
        'Description',
        'Material',
        'Quantity',
        'Unit',
        'Unit Cost',
        'Total Cost',
        'Research Type',
        'Supplier 1 Company',
        'Supplier 1 Price',
        'Supplier 1 MOQ',
        'Supplier 1 Location',
        'Supplier 1 Contact',
        'Supplier 2 Company',
        'Supplier 2 Price',
        'Supplier 2 Location'
      ]);
    } else {
      csvRows.push([
        'BOM Level',
        'Category', 
        'Component Name',
        'Description',
        'Material',
        'Quantity',
        'Unit',
        'Unit Cost',
        'Total Cost'
      ]);
    }

    // Main assembly (Level 0)
    csvRows.push([
      '0',
      'Main Assembly',
      analysisResult.object_name,
      `Complete ${analysisResult.object_name} assembly`,
      'Mixed materials',
      '1',
      'pieces',
      analysisResult.total_estimated_cost.toFixed(2),
      analysisResult.total_estimated_cost.toFixed(2)
    ]);

    // Categories and components (Level 1 and 2)
    analysisResult.main_categories.forEach((category) => {
      // Category as Level 1
      const categoryTotal = category.components.reduce((sum, comp) => sum + comp.estimated_cost, 0);
      csvRows.push([
        '1',
        'Sub-Assembly',
        category.category,
        `${category.category} sub-assembly`,
        'Mixed materials',
        '1',
        'pieces',
        categoryTotal.toFixed(2),
        categoryTotal.toFixed(2)
      ]);

      // Components as Level 2
      category.components.forEach((component, compIdx) => {
        // Find supplier data for this component if it exists
        const supplierInfo = isPremiumData 
          ? findSupplierData(analysisResult.supplier_data, category, compIdx)
          : null;
        
        // Use the current (researched) price for CSV export
        const unitCost = (component.estimated_cost / component.quantity).toFixed(2);
        const totalCost = component.estimated_cost.toFixed(2);
        
        const baseRow = [
          '2',
          category.category,
          component.name,
          component.description,
          component.material,
          component.quantity,
          component.unit || 'pieces',
          unitCost,
          totalCost
        ];
        
        if (isPremiumData) {
          const extendedRow = [
            ...baseRow,
            component.research_type || 'standard',
            supplierInfo?.supplier1?.company || '',
            supplierInfo?.supplier1?.price || '',
            supplierInfo?.supplier1?.moq || '',
            supplierInfo?.supplier1?.location || '',
            supplierInfo?.supplier1?.contact || '',
            supplierInfo?.supplier2?.company || '',
            supplierInfo?.supplier2?.price || '',
            supplierInfo?.supplier2?.location || ''
          ];
          csvRows.push(extendedRow);
        } else {
          csvRows.push(baseRow);
        }
      });
    });

    // Construction costs as Level 1 processes
    if (analysisResult.construction_costs && analysisResult.construction_costs.length > 0) {
      analysisResult.construction_costs.forEach(cost => {
        csvRows.push([
          '1',
          'Manufacturing Process',
          cost.process,
          cost.description,
          'Labor/Service',
          '1',
          'process',
          cost.cost.toFixed(2),
          cost.cost.toFixed(2)
        ]);
      });
    }

    // Convert to CSV string
    const csvContent = csvRows.map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${analysisResult.object_name}_BOM.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Toggle category expansion
  const toggleCategory = (categoryIndex) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryIndex]: !prev[categoryIndex]
    }));
  };

  // Theme styles - Matching breakdownxai.vercel.app landing page design system
  const theme = {
    bg: isDarkMode ? 'bg-[#101218]' : 'bg-white',
    cardBg: isDarkMode ? 'bg-[rgba(231,236,235,0.08)]' : 'bg-white',
    text: isDarkMode ? 'text-[#E8F0EC]' : 'text-black',
    textSecondary: isDarkMode ? 'text-[#E8F0EC]/70' : 'text-gray-600',
    border: isDarkMode ? 'border-white/[0.08]' : 'border-gray-200',
    hover: isDarkMode ? 'hover:bg-[#282829]/50' : 'hover:bg-gray-50',
    accent: 'text-[#101218]',
    shadow: isDarkMode ? 'shadow-black/20' : 'shadow-gray-100',
    materialBg: isDarkMode ? 'bg-[#282829]/40' : 'bg-gray-50',
    materialBorder: isDarkMode ? 'border-white/[0.04]' : 'border-gray-200'
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text} transition-colors duration-300`}>
      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        .fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        
        .slide-in {
          animation: slideIn 0.5s ease-out;
        }
        
        .slide-in-left {
          animation: slideInLeft 0.6s ease-out;
        }
        
        .slide-in-right {
          animation: slideInRight 0.6s ease-out;
        }
        
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        .gradient-border {
          position: relative;
          background: linear-gradient(135deg, rgba(107,250,200,0.4) 0%, rgba(61,217,165,0.2) 100%);
          padding: 1px;
          border-radius: 0.75rem;
        }
        .gradient-border > div {
          background: #101218 !important;
        }
        `
      }} />

      {/* Header */}
      <header className={`border-b ${theme.border} backdrop-blur-sm sticky top-0 z-50`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Package className="w-6 h-6 text-[#6BFAC8]" />
            <h1 className="text-xl font-semibold">BreakdownAI</h1>
          </div>
          <div className="flex items-center gap-3">
            {authLoading ? (
              <div className="w-8 h-8 rounded-full bg-gray-300 animate-pulse"></div>
            ) : user ? (
              <UserProfile 
                user={user} 
                onSignOut={handleSignOut}
                onShowPricing={() => setShowPricingModal(true)}
              />
            ) : (
              <button
                onClick={() => {
                  setIsPostAnalysisPrompt(false);
                  setShowAuthModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-[#6BFAC8] hover:bg-[#3DD9A5] text-[#101218] rounded-full transition-colors"
              >
                <User className="w-4 h-4" />
                Sign In
              </button>
            )}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg ${theme.hover} transition-colors`}
              aria-label="Toggle theme"
            >
              {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Upload Section */}
        <div className="text-center mb-12 fade-in">
          <h2 className="text-3xl font-bold mb-4">
            Discover What's Inside Any Object
          </h2>
          <p className={`${theme.textSecondary} text-lg`}>
            Upload an image and our AI will break down its components, materials, and costs
          </p>
        </div>

        {/* Upload Area */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="gradient-border">
            <div className={`${theme.cardBg} rounded-xl p-8`}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple
                onChange={handleFileUpload}
                className="hidden"
              />
              
              <div
                onClick={() => {
                  if (!user && anonymousUsageCount >= ANONYMOUS_USE_LIMIT) {
                    setIsPostAnalysisPrompt(true);
                    setShowAuthModal(true);
                  } else {
                    fileInputRef.current?.click();
                  }
                }}
                className={`border-2 border-dashed ${theme.border} rounded-lg p-12 text-center cursor-pointer ${theme.hover} transition-all hover:border-[#6BFAC8]/40`}
              >
                {imagePreviews.length > 0 ? (
                  <div className="space-y-4">
                    <div className={`grid ${imagePreviews.length === 1 ? 'grid-cols-1' : imagePreviews.length === 2 ? 'grid-cols-2' : 'grid-cols-2'} gap-4`}>
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="relative">
                          <img
                            src={preview}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg shadow-lg"
                          />
                          <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1 rounded">
                            Angle {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className={`${theme.textSecondary} text-sm`}>
                      {imagePreviews.length} image{imagePreviews.length > 1 ? 's' : ''} uploaded - Click to upload different images
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(user || anonymousUsageCount < ANONYMOUS_USE_LIMIT) ? (
                      <>
                        <Upload className="w-12 h-12 mx-auto text-[#6BFAC8]" />
                        <div>
                          <p className="text-lg font-medium">Drop your images here</p>
                          <p className={`${theme.textSecondary} text-sm mt-1`}>
                            or click to browse • Upload 1-4 images from different angles
                          </p>
                          <p className={`${theme.textSecondary} text-xs`}>
                            (JPG, PNG, WebP - Max 10MB each)
                          </p>
                          {!user && (
                            <p className={`${theme.textSecondary} text-xs mt-2`}>
                              Try 1 free analysis — no sign-in required
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <User className="w-12 h-12 mx-auto text-[#6BFAC8]" />
                        <div>
                          <p className="text-lg font-medium">Sign in to continue analyzing</p>
                          <p className={`${theme.textSecondary} text-sm mt-1`}>
                            You've used your free analysis — sign in to keep going
                          </p>
                          <p className={`${theme.textSecondary} text-xs`}>
                            Free tier: 3 analyses every 10 hours
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="mt-6 text-center space-y-3 fade-in">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#6BFAC8]" />
                  <p className={`${theme.textSecondary} text-sm`}>{currentStep}</p>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3 fade-in">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Object Selection Section */}
        {detectedObjects && (
          <div className="max-w-2xl mx-auto mb-12 fade-in">
            <div className={`${theme.cardBg} rounded-xl p-6`}>
              <h3 className="text-xl font-semibold mb-4 text-center">
                Multiple objects detected! Select objects to analyze:
              </h3>
              <div className="grid gap-3 mb-6">
                {detectedObjects.map((obj, idx) => (
                  <div
                    key={idx}
                    onClick={() => toggleObjectSelection(obj.name)}
                    className={`p-4 rounded-lg border cursor-pointer transition-all ${
                      selectedObjects.includes(obj.name)
                        ? `border-blue-500 bg-blue-500/10 ${theme.hover}`
                        : `${theme.border} ${theme.hover}`
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 border-2 rounded flex items-center justify-center mt-0.5 ${
                        selectedObjects.includes(obj.name)
                          ? 'border-blue-500 bg-blue-500'
                          : `${theme.border.replace('border-', 'border-2 ')}`
                      }`}>
                        {selectedObjects.includes(obj.name) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium">{obj.name}</h4>
                            <p className={`${theme.textSecondary} text-sm mt-1`}>
                              {obj.description}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${
                            obj.confidence === 'High' ? 'bg-[#6BFAC8]/10 text-[#6BFAC8]/80' :
                            obj.confidence === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {obj.confidence}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Selection Summary and Confirm Button */}
              <div className="flex items-center justify-between">
                <p className={`${theme.textSecondary} text-sm`}>
                  {selectedObjects.length} object{selectedObjects.length !== 1 ? 's' : ''} selected
                </p>
                <button
                  onClick={confirmSelectedObjects}
                  disabled={selectedObjects.length === 0 || isLoading}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    selectedObjects.length > 0 && !isLoading
                      ? 'bg-[#6BFAC8] hover:bg-[#3DD9A5] text-[#101218]'
                      : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? 'Analyzing...' : 'Confirm & Analyze'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results Section - Side by Side Layout */}
        {analysisResult && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 fade-in">
            <div className={`${theme.bg} h-full flex flex-col`}>
              {/* Header with Close Button */}
              <div className={`border-b ${theme.border} p-4 flex justify-between items-center`}>
                <div className="flex items-center gap-4">
                  <h2 className="text-2xl font-bold">{analysisResult.object_name}</h2>
                  <div className="flex gap-6">
                    <div className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-[#6BFAC8]" />
                      <span className="font-medium">{getTotalComponents(analysisResult)} Parts</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      <span className="font-medium">${analysisResult.total_estimated_cost} Total</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-purple-500" />
                      <span className="font-medium">{analysisResult.assembly_complexity} Assembly</span>
                    </div>
                    {isResearchingPrices && (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className={`text-sm ${theme.textSecondary}`}>
                          {researchProgress || 'Researching prices...'}
                        </span>
                      </div>
                    )}
                    
                    {!isResearchingPrices && (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => startPriceResearch(analysisResult)}
                          className="px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-900 hover:border-gray-700 flex items-center gap-2 shadow-sm hover:shadow-md"
                        >
                          <DollarSign className="w-4 h-4" />
                          Research Real Prices
                          <span className="text-xs text-gray-500">(3-5 min)</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportToCSV}
                    className={`p-2 rounded-lg ${theme.hover} transition-colors`}
                    title="Export BOM to CSV"
                    aria-label="Export BOM to CSV"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={resetToMain}
                    className={`p-2 rounded-lg ${theme.hover} transition-colors`}
                    aria-label="Close analysis"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Side by Side Content */}
              <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                {/* Results Side - Left on desktop, top on mobile */}
                <div className="w-full lg:w-1/2 overflow-y-auto p-4 lg:p-8 slide-in-left order-2 lg:order-1">
                  <div className="space-y-4 lg:space-y-6">
                    {/* Component Categories - Collapsible */}
                    {analysisResult.main_categories.map((category, idx) => {
                      const isExpanded = expandedCategories[idx] === true; // Default to collapsed
                      return (
                        <div key={idx} className={`${theme.cardBg} border ${theme.border} rounded-lg shadow-sm ${theme.shadow}`}>
                          {/* Category Header - Clickable */}
                          <button
                            onClick={() => toggleCategory(idx)}
                            className={`w-full p-4 lg:p-6 text-left ${theme.hover} transition-colors rounded-lg focus:outline-none`}
                          >
                            <div className="flex items-center justify-between">
                              <h4 className="text-base lg:text-lg font-semibold flex items-center gap-3">
                                <span className={`w-2 h-2 rounded-full bg-black`}></span>
                                {category.category}
                              </h4>
                              {isExpanded ? 
                                <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              }
                            </div>
                          </button>
                          
                          {/* Category Content - Collapsible */}
                          {isExpanded && (
                            <div className="px-4 lg:px-6 pb-4 lg:pb-6 pt-4 lg:pt-6 space-y-3 border-t border-gray-100">
                              {category.components.map((component, compIdx) => (
                                <div
                                  key={compIdx}
                                  className={`p-4 rounded-lg border ${theme.border} ${theme.hover} transition-all duration-200`}
                                >
                                  <div className="flex justify-between items-start mb-3">
                                    <h5 className="font-medium text-sm lg:text-base">{component.name}</h5>
                                    <div className="text-right">
                                      {component.price_updated && component.original_cost ? (
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm text-gray-500 line-through">
                                            ${component.original_cost.toFixed(2)}
                                          </span>
                                          <button 
                                            className={`font-semibold text-lg cursor-pointer hover:underline focus:outline-none ${
                                              component.research_type === 'premium' ? 'text-[#6BFAC8] hover:text-[#3DD9A5]' : 'text-[#6BFAC8]/80 hover:text-[#6BFAC8]'
                                            }`}
                                            onClick={() => showSourceDetails(component)}
                                          >
                                            ${component.estimated_cost}
                                          </button>
                                          {component.research_type === 'premium' && (
                                            <span className="text-xs text-purple-500">✨</span>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="font-semibold text-lg">${component.estimated_cost}</span>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <p className={`${theme.textSecondary} text-xs lg:text-sm mb-3 leading-relaxed`}>
                                    {component.description}
                                  </p>
                                  
                                  {/* Enhanced Material & Quantity Display */}
                                  <div className="grid grid-cols-3 gap-4 text-xs lg:text-sm">
                                    <div className={`${theme.materialBg} border ${theme.materialBorder} p-3 rounded`}>
                                      <span className={`font-semibold ${theme.text} block mb-1`}>Material</span>
                                      <span className={`${theme.textSecondary} text-sm font-medium`}>{component.material}</span>
                                    </div>
                                    <div className={`${theme.materialBg} border ${theme.materialBorder} p-3 rounded`}>
                                      <span className={`font-semibold ${theme.text} block mb-1`}>Quantity</span>
                                      <span className={`${theme.textSecondary} text-sm font-medium`}>
                                        {component.quantity} {component.unit || 'pieces'}
                                      </span>
                                    </div>
                                    <div className={`${theme.materialBg} border ${theme.materialBorder} p-3 rounded`}>
                                      <span className={`font-semibold ${theme.text} block mb-1`}>Unit Cost</span>
                                      {component.price_updated && component.original_cost ? (
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500 line-through">
                                            ${(component.original_cost / component.quantity).toFixed(2)}
                                          </span>
                                          <button 
                                            className={`text-sm font-medium cursor-pointer hover:underline focus:outline-none ${
                                              component.research_type === 'premium' ? 'text-[#6BFAC8] hover:text-[#3DD9A5]' : 'text-[#6BFAC8]/80 hover:text-[#6BFAC8]'
                                            }`}
                                            onClick={() => showSourceDetails(component)}
                                          >
                                            ${(component.estimated_cost / component.quantity).toFixed(2)}
                                          </button>
                                        </div>
                                      ) : (
                                        <span className={`${theme.textSecondary} text-sm font-medium`}>
                                          ${(component.estimated_cost / component.quantity).toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Construction Costs */}
                    {analysisResult.construction_costs && analysisResult.construction_costs.length > 0 && (
                      <div className={`${theme.cardBg} rounded-xl p-4 lg:p-6`}>
                        <h4 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4 flex items-center gap-2">
                          <Wrench className="w-4 h-4 lg:w-5 lg:h-5 text-[#6BFAC8]" />
                          Construction Costs
                        </h4>
                        <div className="space-y-3">
                          {analysisResult.construction_costs.map((cost, idx) => (
                            <div key={idx} className={`p-3 rounded border ${theme.border} ${theme.hover}`}>
                              <div className="flex justify-between items-center">
                                <span className={`${theme.text} font-medium`}>{cost.process}</span>
                                <span className={`${theme.text} font-semibold`}>${cost.cost}</span>
                              </div>
                              <p className={`${theme.textSecondary} text-xs lg:text-sm mt-1`}>
                                {cost.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action Button */}
                    <div className="text-center py-4 lg:py-6">
                      <button
                        onClick={resetToMain}
                        className="inline-flex items-center gap-2 px-4 lg:px-6 py-2 lg:py-3 bg-white hover:bg-gray-100 text-black rounded-lg transition-colors text-sm lg:text-base border border-gray-300"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Analyze Another Object
                      </button>
                    </div>
                  </div>
                </div>

                {/* Image Side - Right on desktop, bottom on mobile */}
                <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-8 slide-in-right order-1 lg:order-2">
                  <div className="w-full h-full flex items-center justify-center relative">
                    {imagePreviews.length > 0 && (
                      <>
                        <img
                          src={imagePreviews[currentImageIndex]}
                          alt={`Analyzed object - Angle ${currentImageIndex + 1}`}
                          className="max-w-full max-h-[850px] lg:max-h-[calc(100vh-200px)] object-contain rounded-lg shadow-2xl"
                          style={{ maxHeight: '850px' }}
                        />
                        
                        {/* Slideshow controls - only show if multiple images */}
                        {imagePreviews.length > 1 && (
                          <>
                            <button
                              onClick={prevImage}
                              className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${theme.cardBg} ${theme.hover} p-2 rounded-full shadow-lg transition-all`}
                              aria-label="Previous image"
                            >
                              <ChevronLeft className="w-6 h-6" />
                            </button>
                            <button
                              onClick={nextImage}
                              className={`absolute right-2 top-1/2 transform -translate-y-1/2 ${theme.cardBg} ${theme.hover} p-2 rounded-full shadow-lg transition-all`}
                              aria-label="Next image"
                            >
                              <ChevronRight className="w-6 h-6" />
                            </button>
                            
                            {/* Image counter */}
                            <div className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 ${theme.cardBg} px-3 py-1 rounded-full text-sm`}>
                              {currentImageIndex + 1} / {imagePreviews.length}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Source Details Modal */}
      {showSourceModal && selectedComponentSource && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`${theme.bg} ${theme.border} border rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto shadow-xl`}>
            {/* Modal Header */}
            <div className={`p-4 border-b ${theme.border} flex justify-between items-center`}>
              <h3 className="text-lg font-semibold">Price Source Details</h3>
              <button 
                onClick={closeSourceModal}
                className={`p-2 rounded-lg ${theme.hover} transition-colors`}
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-4 space-y-4">
              {/* Component Info */}
              <div className={`p-3 rounded border ${theme.border} ${theme.hover}`}>
                <h4 className="font-medium mb-2">{selectedComponentSource.component.name}</h4>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Material:</span>
                    <span>{selectedComponentSource.component.material}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Quantity:</span>
                    <span>{selectedComponentSource.component.quantity} {selectedComponentSource.component.unit || 'pieces'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={theme.textSecondary}>Current Price:</span>
                    <span className="font-semibold">${selectedComponentSource.component.estimated_cost}</span>
                  </div>
                </div>
              </div>

              {/* Research Type & Source Info */}
              <div className={`p-3 rounded border ${theme.border} space-y-3`}>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    selectedComponentSource.researchType === 'premium' 
                      ? 'bg-[#6BFAC8]/15 text-[#6BFAC8]' 
                      : 'bg-[#6BFAC8]/10 text-[#6BFAC8]/80'
                  }`}>
                    {selectedComponentSource.researchType === 'premium' ? 'Premium Research' : 'Standard Research'}
                  </span>
                  {selectedComponentSource.researchType === 'premium' && (
                    <span className="text-xs text-purple-500">✨</span>
                  )}
                </div>

                {selectedComponentSource.researchType === 'premium' && selectedComponentSource.suppliers?.length > 0 ? (
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium">Supplier Sources:</h5>
                    {selectedComponentSource.suppliers.slice(0, 2).map((supplier, idx) => (
                      <div key={idx} className={`p-2 rounded border ${theme.border} ${theme.hover}`}>
                        <div className="text-xs space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{supplier.company || 'Unknown Company'}</span>
                            <span className={`px-1.5 py-0.5 text-xs rounded ${theme.hover}`}>
                              {supplier.source || 'Alibaba'}
                            </span>
                          </div>
                          {supplier.location && (
                            <div className="flex justify-between">
                              <span className={theme.textSecondary}>Location:</span>
                              <span>{supplier.location}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className={theme.textSecondary}>Price:</span>
                            <span className="font-medium">${supplier.price_per_unit || 'N/A'}</span>
                          </div>
                          {supplier.moq && (
                            <div className="flex justify-between">
                              <span className={theme.textSecondary}>MOQ:</span>
                              <span>{supplier.moq}</span>
                            </div>
                          )}
                          {supplier.contact && (
                            <div className="flex justify-between">
                              <span className={theme.textSecondary}>Contact:</span>
                              <span className="text-xs truncate">{supplier.contact}</span>
                            </div>
                          )}
                          {supplier.product_url && supplier.product_url !== 'N/A' && supplier.product_url.startsWith('http') && (
                            <div className="mt-1">
                              <a 
                                href={supplier.product_url}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#6BFAC8]/80 hover:text-[#6BFAC8] underline text-xs"
                              >
                                View Product Page →
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Source:</span>
                      <span>
                        {selectedComponentSource.component.source || 
                          (selectedComponentSource.researchType === 'premium' 
                            ? 'Alibaba & 1688 suppliers' 
                            : 'Alibaba & 1688 market data'
                          )
                        }
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={theme.textSecondary}>Method:</span>
                      <span>
                        {selectedComponentSource.researchType === 'premium' 
                          ? 'Real-time supplier search' 
                          : 'AI-powered wholesale price analysis'
                        }
                      </span>
                    </div>
                    {selectedComponentSource.component.source && selectedComponentSource.researchType === 'standard' && (
                      <div className={`mt-2 p-2 rounded border ${theme.border} ${theme.hover}`}>
                        <div className="text-xs">
                          <span className={`${theme.textSecondary} font-medium`}>Research Details:</span>
                          <div className="mt-1">{selectedComponentSource.component.source}</div>
                          {selectedComponentSource.component.source_url && 
                           selectedComponentSource.component.source_url !== 'N/A' && 
                           selectedComponentSource.component.source_url.startsWith('http') && (
                            <div className="mt-2">
                              <a 
                                href={selectedComponentSource.component.source_url}
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[#6BFAC8]/80 hover:text-[#6BFAC8] underline text-xs"
                              >
                                View Product Page →
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setIsPostAnalysisPrompt(false);
        }}
        onAuthSuccess={handleAuthSuccess}
        isPostAnalysis={isPostAnalysisPrompt}
      />

      {/* Pricing Modal */}
      <PricingModal 
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        currentTier={user ? 'free' : 'free'} // This will be updated with real user data
        onUpgrade={(tier) => {
          console.log('Upgrading to:', tier);
          setShowPricingModal(false);
        }}
      />
    </div>
  );
};

export default BreakdownAI;